/**
 * HIPAA Compliance Dashboard
 * Admin view for monitoring compliance status, audit logs, and user access
 */
import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import api from '../api';
import { toast } from 'sonner';
import {
  Shield, CheckCircle, XCircle, AlertTriangle, Users, Lock, Eye,
  FileText, Clock, Activity, Download, RefreshCw, Search, Filter,
  UserCheck, Key, BarChart3
} from 'lucide-react';

const HIPAAComplianceDashboard = () => {
  const [complianceStatus, setComplianceStatus] = useState(null);
  const [auditStats, setAuditStats] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(false);
  const [filters, setFilters] = useState({
    eventType: '',
    startDate: '',
    endDate: ''
  });
  const [pagination, setPagination] = useState({ total: 0, limit: 50, skip: 0 });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [complianceRes, statsRes] = await Promise.all([
        api.get('/hipaa/compliance-status'),
        api.get('/hipaa/audit-logs/stats')
      ]);

      if (complianceRes.data.success) {
        setComplianceStatus(complianceRes.data.compliance);
      }
      if (statsRes.data.success) {
        setAuditStats(statsRes.data.stats);
      }
    } catch (error) {
      console.error('Error loading compliance data:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied. Admin privileges required.');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadAuditLogs = async (skip = 0) => {
    setLogsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.eventType) params.append('event_type', filters.eventType);
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      params.append('limit', pagination.limit);
      params.append('skip', skip);

      const response = await api.get(`/hipaa/audit-logs?${params.toString()}`);
      
      if (response.data.success) {
        setAuditLogs(response.data.logs);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Error loading audit logs:', error);
      toast.error('Failed to load audit logs');
    } finally {
      setLogsLoading(false);
    }
  };

  const exportAuditLogs = () => {
    const headers = ['Timestamp', 'Event Type', 'User', 'IP Address', 'Resource', 'Details', 'Success'];
    const rows = auditLogs.map(log => [
      log.timestamp,
      log.eventType,
      log.userEmail,
      log.ipAddress,
      log.resourceType || '-',
      JSON.stringify(log.details || {}),
      log.success ? 'Yes' : 'No'
    ]);

    const csvContent = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hipaa-audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Audit logs exported');
  };

  const eventTypeLabels = {
    'LOGIN_SUCCESS': { label: 'Login Success', color: 'green' },
    'LOGIN_FAILED': { label: 'Login Failed', color: 'red' },
    'LOGOUT': { label: 'Logout', color: 'gray' },
    'PASSWORD_CHANGE': { label: 'Password Change', color: 'blue' },
    'ACCOUNT_LOCKED': { label: 'Account Locked', color: 'red' },
    'ACCOUNT_UNLOCKED': { label: 'Account Unlocked', color: 'green' },
    'TWO_FACTOR_SUCCESS': { label: '2FA Success', color: 'green' },
    'TWO_FACTOR_FAILED': { label: '2FA Failed', color: 'red' },
    'PHI_VIEW': { label: 'PHI View', color: 'yellow' },
    'PHI_CREATE': { label: 'PHI Create', color: 'blue' },
    'PHI_UPDATE': { label: 'PHI Update', color: 'orange' },
    'PHI_DELETE': { label: 'PHI Delete', color: 'red' },
    'PATIENT_VIEW': { label: 'Patient View', color: 'yellow' },
    'PATIENT_CREATE': { label: 'Patient Create', color: 'blue' },
    'USER_CREATE': { label: 'User Create', color: 'blue' },
    'ROLE_CHANGE': { label: 'Role Change', color: 'purple' },
    'SETTINGS_CHANGE': { label: 'Settings Change', color: 'gray' },
    'SESSION_TIMEOUT': { label: 'Session Timeout', color: 'orange' }
  };

  const getEventTypeStyle = (eventType) => {
    const config = eventTypeLabels[eventType] || { label: eventType, color: 'gray' };
    const colors = {
      green: 'bg-green-100 text-green-700',
      red: 'bg-red-100 text-red-700',
      blue: 'bg-blue-100 text-blue-700',
      yellow: 'bg-yellow-100 text-yellow-700',
      orange: 'bg-orange-100 text-orange-700',
      purple: 'bg-purple-100 text-purple-700',
      gray: 'bg-gray-100 text-gray-700'
    };
    return { label: config.label, className: colors[config.color] };
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="hipaa-compliance-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Shield className="w-7 h-7 text-blue-600" />
              HIPAA Compliance Dashboard
            </h1>
            <p className="text-gray-500 mt-1">Monitor security, access controls, and audit logs</p>
          </div>
          <Button onClick={loadData} variant="outline" data-testid="refresh-compliance-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Compliance Status Cards */}
        {complianceStatus && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg border p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${complianceStatus.compliant ? 'bg-green-100' : 'bg-red-100'}`}>
                  {complianceStatus.compliant ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-600" />
                  )}
                </div>
                <div>
                  <p className="text-lg font-bold">{complianceStatus.compliant ? 'Compliant' : 'Issues Found'}</p>
                  <p className="text-xs text-gray-500">Overall Status</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg border p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-lg font-bold">{complianceStatus.userStats?.totalUsers || 0}</p>
                  <p className="text-xs text-gray-500">Total Users</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg border p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Key className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-lg font-bold">{complianceStatus.userStats?.twoFactorPercentage || 0}%</p>
                  <p className="text-xs text-gray-500">2FA Enabled</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg border p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <UserCheck className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-lg font-bold">{complianceStatus.userStats?.acknowledgementPercentage || 0}%</p>
                  <p className="text-xs text-gray-500">HIPAA Acknowledged</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Compliance Checks */}
        {complianceStatus?.checks && (
          <div className="bg-white rounded-lg border p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Technical Safeguards Status
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {Object.entries(complianceStatus.checks).map(([check, status]) => (
                <div key={check} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                  {status ? (
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-600" />
                  )}
                  <span className="text-sm capitalize">{check.replace(/_/g, ' ')}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Audit Statistics */}
        {auditStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg border p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Activity className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{auditStats.todayEvents}</p>
                  <p className="text-xs text-gray-500">Events Today</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg border p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <BarChart3 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{auditStats.weekEvents}</p>
                  <p className="text-xs text-gray-500">Events This Week</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg border p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{auditStats.loginFailuresThisWeek}</p>
                  <p className="text-xs text-gray-500">Failed Logins (7d)</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg border p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Eye className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{auditStats.phiAccessThisWeek}</p>
                  <p className="text-xs text-gray-500">PHI Access (7d)</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Audit Logs */}
        <div className="bg-white rounded-lg border">
          <div className="p-4 border-b">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <FileText className="w-5 h-5 text-gray-600" />
                Audit Logs
              </h2>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => loadAuditLogs(0)} data-testid="load-logs-btn">
                  <Search className="w-4 h-4 mr-1" /> Load Logs
                </Button>
                <Button variant="outline" size="sm" onClick={exportAuditLogs} data-testid="export-logs-btn">
                  <Download className="w-4 h-4 mr-1" /> Export CSV
                </Button>
              </div>
            </div>

            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">Event Type</Label>
                <select
                  value={filters.eventType}
                  onChange={(e) => setFilters(prev => ({ ...prev, eventType: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                >
                  <option value="">All Events</option>
                  {Object.entries(eventTypeLabels).map(([key, val]) => (
                    <option key={key} value={key}>{val.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs">Start Date</Label>
                <Input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">End Date</Label>
                <Input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div className="flex items-end">
                <Button onClick={() => loadAuditLogs(0)} className="w-full bg-blue-600 hover:bg-blue-700">
                  <Filter className="w-4 h-4 mr-1" /> Apply Filters
                </Button>
              </div>
            </div>
          </div>

          {/* Logs Table */}
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="audit-logs-table">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Event</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP Address</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {auditLogs.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                      {logsLoading ? 'Loading...' : 'Click "Load Logs" to view audit trail'}
                    </td>
                  </tr>
                ) : (
                  auditLogs.map((log) => {
                    const eventStyle = getEventTypeStyle(log.eventType);
                    return (
                      <tr key={log.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {new Date(log.timestamp).toLocaleString()}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${eventStyle.className}`}>
                            {eventStyle.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-800">{log.userEmail}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono">{log.ipAddress}</td>
                        <td className="px-4 py-3">
                          {log.success ? (
                            <span className="text-green-600 text-sm">✓</span>
                          ) : (
                            <span className="text-red-600 text-sm">✗</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500 max-w-xs truncate">
                          {JSON.stringify(log.details || {})}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.total > 0 && (
            <div className="p-4 border-t bg-gray-50 flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Showing {pagination.skip + 1} - {Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => loadAuditLogs(Math.max(0, pagination.skip - pagination.limit))}
                  disabled={pagination.skip === 0}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => loadAuditLogs(pagination.skip + pagination.limit)}
                  disabled={!pagination.hasMore}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default HIPAAComplianceDashboard;
